
import "./App.css";
import "./Screen.css";
import Img404 from './media/404-illustration.webp';

import React, { useCallback, useEffect, useRef, Suspense } from "react";
import { useHistory, useLocation, Route, Switch, Redirect } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { useSelector } from "react-redux";
import { Layout, Result, Spin } from 'antd';
import { ReactComponent as Logo } from './media/stetho.svg';
import HeaderLinks from "./components/HeaderLinks";
import FooterVitrine from "./components/FooterVitrine";
import LandingPage from "./screens/LandingPage/LandingPage";
import NotreSolutionScreen from "./screens/NotreSolutionScreen/NotreSolutionScreen";
import BlogScreen from "./screens/BlogScreen/BlogScreen";
import ArticleScreen from "./screens/ArticleScreen/ArticleScreen";
import QuiSommesNousScreen from "./screens/QuiSommesNousScreen/QuiSommesNousScreen";
import MentionsLegalesScreen from "./screens/LegalContent/MentionsLegalesScreen";
import PolitiqueDeConfidentialiteScreen from "./screens/LegalContent/PolitiqueDeConfidentialiteScreen";
import CGUScreen from "./screens/LegalContent/CGUScreen";
import ContactScreen from "./screens/ContactScreen/ContactScreen";
import EligibiliteVFCScreen from "./screens/EligibiliteVFCScreen/EligibiliteVFCScreen";
import LoginScreen from "./screens/LoginScreen/LoginScreen";
import LogoutScreen from "./screens/LogoutScreen/LogoutScreen";

import PresentationANS from "./screens/PresentationANS/PresentationANS";
import MainScreen from "./components/MainScreen";
import { useAuth, hasAuthParams } from "react-oidc-context";
import { useAxios } from "./components/hooks/useAxios";
import ErrorFallback from "./components/ErrorFallback";
import PrivateRoute from "./components/PrivateRoute";
import { MEDICAL_ROLES } from "./constants/userConstants";
import { deconnexionEnCours } from "./utils/logoutBroadcast";

const HistoricScreen     = React.lazy(() => import("./screens/HistoricScreen/HistoricScreen"));
const ToolsScreen        = React.lazy(() => import("./screens/ToolsScreen/ToolsScreen"));
const RegisterScreen     = React.lazy(() => import("./screens/RegisterScreen/RegisterScreen"));
const NewsletterScreen   = React.lazy(() => import("./screens/NewsletterScreen/NewsletterScreen"));
const ProfileScreen      = React.lazy(() => import("./screens/ProfileScreen/ProfileScreen"));
const UpdateSalarie       = React.lazy(() => import("./screens/SalarieScreen/UpdateSalarie"));
const UpdateVisite       = React.lazy(() => import("./screens/VisiteScreen/UpdateVisite"));
const UpdatePublicVisite = React.lazy(() => import("./screens/VisiteScreen/UpdatePublicVisite"));
const CreateMailVisite   = React.lazy(() => import("./screens/CreateMailVisite/CreateMailVisite"));
const CreateLettreVisite = React.lazy(() => import("./screens/CreateLettreVisite/CreateLettreVisite"));


const { Header, Content, Footer } = Layout;

// Routes du flux d'authentification : ne jamais y tenter de reconnexion silencieuse.
// - /login  : LoginScreen déclenche déjà signinRedirect, mais sa garde attend la fin de
//             tout activeNavigator — un essai silencieux ne ferait qu'y ajouter l'attente
//             du timeout avant de laisser la redirection partir.
// - /logout : LogoutScreen appelle removeUser() puis signoutRedirect() ; entre les deux,
//             un prompt=none trouverait le cookie SSO encore vivant et reconnecterait
//             l'utilisateur en pleine déconnexion.
const NO_SILENT_SIGNIN_PATHS = ["/login", "/logout"];


function App() {

  const userLogin = useSelector((state) => {
    return state.userLogin
  });
  const { userInfo } = userLogin;

  let history = useHistory();
  const auth = useAuth();
  const axios = useAxios();
  const token = auth.user?.access_token;
  const { pathname, hash } = useLocation();
  const WHITE_PAGE_PREFIXES = ['/blog/', '/notre-solution', '/contact', '/qui-sommes-nous', '/logout', '/presentation-ans'];
  let pageBackground;
  if (pathname === '/blog') {
    pageBackground = '#f7fbfa';
  } else if (pathname === '/' || WHITE_PAGE_PREFIXES.some(prefix => pathname.startsWith(prefix.toLowerCase()))) {
    pageBackground = '#ffffff';
  } else {
    pageBackground = 'var(--vert-clair-nuance-5)';
  }
  const isColoredPage = pageBackground === 'var(--vert-clair-nuance-5)';
  const tokenRef = useRef(token);

  // Scroll au top sur changement de route, ou vers l'ancre si un hash est présent.
  // Le hash est aussi en dépendance pour gérer les clics sur une ancre quand on
  // est déjà sur la page cible (le pathname ne change pas dans ce cas).
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    const id = hash.substring(1);
    // Retry car la position de l'élément peut bouger pendant le chargement
    // des images / animations sal.js. On s'arrête dès que la position est
    // stable sur 2 tentatives consécutives, ou après ~1s.
    let lastTop = null;
    let stableCount = 0;
    const delays = [0, 150, 400, 800];
    const timeouts = delays.map(delay => setTimeout(() => {
      const element = document.getElementById(id);
      if (!element) return;
      const top = element.getBoundingClientRect().top + window.scrollY;
      if (top === lastTop) {
        stableCount += 1;
        if (stableCount >= 2) return;
      } else {
        stableCount = 0;
        lastTop = top;
      }
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }, delay));

    return () => timeouts.forEach(clearTimeout);
  }, [pathname, hash]);

  // Référence pour récupérer le token à jour après un refresh
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Reconnexion auto (Par exemple si on ouvre un nouvel onglet)
  useEffect(() => {
    if (auth.isLoading || auth.activeNavigator) return; // ne pas concurrencer un flux en cours
    if (hasAuthParams()) return;                        // page de callback OIDC (?code&state)
    if (auth.isAuthenticated || auth.user) return;      // déjà un user
    if (auth.error) return;
    if (NO_SILENT_SIGNIN_PATHS.some((p) => pathname.startsWith(p))) return;
    // Une déconnexion est en cours dans un autre onglet : un prompt=none trouverait le
    // cookie SSO encore vivant et recréerait une session (cf. utils/logoutBroadcast.js).
    if (deconnexionEnCours()) return;
    // Ne tenter la reconnexion que si ce navigateur a déjà eu une session RAVAIL. Sinon
    // tout visiteur anonyme (accueil, blog, contact...) déclenche une iframe prompt=none
    // inutile qui se solde par un auth.error persistant. `userInfo` est le marqueur :
    // posé par userActions.login, retiré par userActions.logout / LoginScreen.cleanAuth /
    // useAxios.redirectToLogin.
    if (!localStorage.getItem("userInfo")) return;
    // .catch silencieux : un échec de renew (ex: iframe bloquée par l'ITP iOS) ne doit
    // ni déconnecter ni déclencher le handler unhandledrejection (redirection /500).
    auth.signinSilent().catch(() => {});
  }, [auth.isLoading, auth.isAuthenticated, auth.activeNavigator, auth.error, pathname]);

  // Fonction qui permet de logger les erreurs non gérées du front-end
  const onError = useCallback((error, info) => {
    
    console.error(error, info);

    if(tokenRef.current !== undefined) {
      // Pour toutes les erreurs non gérées du front-end, on les envoie au back-end pour les enregistrer dans un fichier de log

      try{
        axios.post("/api/log/error", { error: (error?.stack ? error.stack : JSON.stringify(error)), info }).then((data) => {
      
          if (data.status === 201) {
            console.log("Erreur logguée avec succès");
          }
          else {
            if(data.status === 401) {
              console.log("Veuillez vous connecter pour pouvoir logguer l'erreur");
              return;
            }
            else{
              console.log("Erreur lors de l'envoi des informations d'erreurs. Nouvelle tentative dans 5s");
              // On réessaie dans 5s jusqu'à ce que ça marche
              setTimeout(() => {
                onError(error, info);
              }, 5000);
            }
          }
        }).catch((err) => {
          if(err?.response?.status === 401) {
            console.log("Veuillez vous connecter pour pouvoir logguer l'erreur");
            return;
          }
          else{
            console.log("Erreur lors de l'envoi des informations d'erreurs. Nouvelle tentative dans 5s");
            // On réessaie dans 5s jusqu'à ce que ça marche
            setTimeout(() => {
              onError(err);
            }, 5000);
          }
        });
      }
      catch(e){
        console.log("Erreur lors de l'envoi des informations d'erreurs. Nouvelle tentative dans 5s");
        // On réessaie dans 5s jusqu'à ce que ça marche
        setTimeout(() => {
          onError(error, info);
        }, 5000);
      }
    }
  }, []);

  useEffect(() => {
    window.addEventListener("unhandledrejection", function (event){
      onError(event?.reason, event?.promise);
      // On redirige vers la page d'erreur 500
      this.window.location.href = "/500";
    });
  }, [onError, history]);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
      <Layout className="layout">
        <div className="background">
          <Header style={{ background: "rgb(255 255 255 / 0%)", zIndex: "10", height: 0, position: "relative" }}>
            <HeaderLinks />
          </Header>
          <Content>
            <main id="App" className="App" style={{ paddingBottom: isColoredPage ? "10px" : 0 }}>
              <Suspense fallback={<div className='conteneur-flex'><Spin size="large" indicator={<Logo id="beatingLogo" style={{ fontSize: 94 }}/>}/></div>}>
              <Switch>
                {/* Routes Publics */}
                <Route
                  path="/"
                  exact
                  render={(props) => 
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError} >
                      <LandingPage {...props} />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/login"
                  render={(props) =>
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
                      <LoginScreen {...props} />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/logout"
                  render={(props) =>
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
                      <LogoutScreen {...props} />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/blog/:slug"
                  render={(props) =>
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
                      <ArticleScreen {...props} />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/blog"
                  render={(props) =>
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
                      <BlogScreen {...props} />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/notre-solution"
                  render={(props) =>
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
                      <NotreSolutionScreen {...props} />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/contact"
                  render={(props) =>
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
                      <ContactScreen {...props} />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/test-eligibilite-vfc"
                  render={(props) =>
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
                      <EligibiliteVFCScreen {...props} />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/qui-sommes-nous"
                  render={(props) =>
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
                      <QuiSommesNousScreen {...props} />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/presentation-ANS"
                  render={(props) =>
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
                      <PresentationANS {...props} />
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/mentions-legales"
                  render={(props) =>
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
                      <MainScreen>
                        <MentionsLegalesScreen {...props} />
                      </MainScreen>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/politique-de-confidentialite"
                  render={(props) =>
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
                      <MainScreen>
                        <PolitiqueDeConfidentialiteScreen {...props} />
                      </MainScreen>
                    </ErrorBoundary>
                  }
                />
                <Route
                  path="/cgu"
                  render={(props) =>
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
                      <MainScreen>
                        <CGUScreen {...props} />
                      </MainScreen>
                    </ErrorBoundary>
                  }
                />
                <PrivateRoute
                  path="/salarie/:shortId"
                  userInfo={userInfo}
                  rolesAllowed={[...MEDICAL_ROLES, "GESTIONNAIRE_ADMINISTRATIF", "SECRETAIRE", "PREVENTEUR"]}
                  privateComponent={UpdateSalarie}
                />
                {/* Routes publiques avec accès limité */}
                <Route
                  path="/visite/:shortId/:tokenForLimitedAccess"
                  render={(props) =>
                    <ErrorBoundary FallbackComponent={ErrorFallback} onError={onError}>
                      <UpdatePublicVisite {...props}/>
                    </ErrorBoundary>
                  }
                />
                {/* Routes privées (connexion obligatoire) */}
                <PrivateRoute
                  path="/visite/:shortId"
                  userInfo={userInfo}
                  rolesAllowed={MEDICAL_ROLES}
                  privateComponent={UpdateVisite}
                />
                <PrivateRoute
                  path="/email"
                  userInfo={userInfo}
                  rolesAllowed={["SUPER_ADMIN", "ADMIN", "MEDECIN", "INTERNE", "INFIRMIER", "ASSISTANT_MEDICAL", "PREVENTEUR", "GESTIONNAIRE_ADMINISTRATIF", "SECRETAIRE"]}
                  privateComponent={CreateMailVisite}
                />
                <PrivateRoute
                  path="/lettre"
                  userInfo={userInfo}
                  rolesAllowed={["SUPER_ADMIN", "ADMIN", "MEDECIN", "INTERNE", "INFIRMIER", "ASSISTANT_MEDICAL", "PREVENTEUR", "GESTIONNAIRE_ADMINISTRATIF", "SECRETAIRE"]}
                  privateComponent={CreateLettreVisite}
                />
                {/* Routes gestion */}
                <PrivateRoute
                  path="/profile"
                  userInfo={userInfo}
                  rolesAllowed={["SUPER_ADMIN", "ADMIN", "MEDECIN", "INFIRMIER", "ASSISTANT_MEDICAL", "PREVENTEUR", "GESTIONNAIRE_ADMINISTRATIF", "ADMIN_MATRICES", "ADMIN_STATISTIQUES", "SECRETAIRE"]}
                  privateComponent={ProfileScreen}
                />
                <PrivateRoute
                  path="/search"
                  userInfo={userInfo}
                  rolesAllowed={MEDICAL_ROLES}
                  privateComponent={HistoricScreen}
                />
                <PrivateRoute
                  path="/visie"
                  userInfo={userInfo}
                  rolesAllowed={["SUPER_ADMIN", "ADMIN", "MEDECIN", "INFIRMIER", "ASSISTANT_MEDICAL", "PREVENTEUR", "GESTIONNAIRE_ADMINISTRATIF", "ADMIN_MATRICES", "ADMIN_STATISTIQUES", "SECRETAIRE"]}
                  privateComponent={ToolsScreen}
                />
                <Route
                  path="/tools"
                  render={() => <Redirect to="/visie" />}
                />
                {/* Routes administrateurs */}
                <PrivateRoute
                  path="/register"
                  userInfo={userInfo}
                  adminOnly={true}
                  privateComponent={RegisterScreen}
                />
                <PrivateRoute
                  path="/newsletter"
                  userInfo={userInfo}
                  adminOnly={true}
                  privateComponent={NewsletterScreen}
                />
                {/* Route d'erreurs*/}
                <Route path="/401" component={() => (
                    <MainScreen>
                      <Result
                        status="401"
                        title="Erreur 401 : Désolé, vous n'êtes pas autorisé à accéder à cette page."
                        subTitle={
                          <>
                            <span>Essayez de vous déconnecter/reconnecter et/ou de vous connecter avec un autre compte !</span><br/>
                            <span>Si cela ne fonctionne toujours pas, merci de nous contacter, cela nous aiderait énormément !</span><br/>
                            <span>Nous sommes une petite équipe et nous faisons de notre mieux pour corriger les erreurs.</span>
                          </>
                        }
                      />
                    </MainScreen>
                  )}
                />
                <Route path="/403" component={() => (
                    <MainScreen>
                      <Result
                        status="403"
                        title="Erreur 403 : Désolé, vous n'êtes pas autorisé à accéder à cette page."
                        subTitle={
                          <>
                            <span>Essayez de vous déconnecter/reconnecter et/ou de vous connecter avec un autre compte !</span><br/>
                            <span>Si cela ne fonctionne toujours pas, merci de nous contacter, cela nous aiderait énormément !</span><br/>
                            <span>Nous sommes une petite équipe et nous faisons de notre mieux pour corriger les erreurs.</span>
                          </>
                        }
                      />
                    </MainScreen>
                  )}
                />
                <Route path="/500" component={() => (
                    <MainScreen>
                      <Result
                        status="500"
                        title="Erreur 500 : Désolé, une erreur interne est survenue"
                        subTitle={
                          <>
                            <span>Veuillez ré-essayer. Si cela ne fonctionne toujours pas, merci de nous contacter, cela nous aiderait énormément !</span><br/>
                            <span>Nous sommes une petite équipe et nous faisons de notre mieux pour corriger les erreurs.</span>
                          </>
                        }
                      />
                    </MainScreen>
                  )}
                />
                <Route component={() => (
                    <div className="nf-page">
                      <div className="nf-content">
                        <span className="nf-number">404</span>
                        <h1 className="nf-title">Oups ! Page introuvable.</h1>
                        <p className="nf-desc">
                          Si vous pensez qu'il s'agit d'une erreur, contactez-nous pour nous aider à la corriger. Merci !
                        </p>
                        <div className="nf-actions">
                          <a href="/" className="nf-btn nf-btn--primary">Retour à l'accueil</a>
                          <a href="/contact" className="nf-btn nf-btn--secondary">Nous contacter</a>
                        </div>
                        <img src={Img404} alt="Page introuvable" className="nf-illustration" />
                      </div>
                    </div>
                  )}
                />
              </Switch>
              </Suspense>
            </main>
          </Content>
        </div>
        <Footer className="footer-vitrine-wrapper"><FooterVitrine waveBackground={pageBackground} /></Footer>
      </Layout>
    </ErrorBoundary>
  );
}

export default App;
